// Libraries
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useEffect} from '@supermove/hooks';
import {Typography, colors} from '@supermove/styles';
import {Phone} from '@supermove/utils';

// App
import DropdownSheetOption from '@shared/design/components/DropdownInput/components/DropdownSheetOption';
import FieldInput from '@shared/design/components/Field/FieldInput';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  overflow: visible;
  justify-content: center;
`;

const SearchText = Styled.Text`
  ${Typography.Responsive.Heading1}
`;

const CalleeText = Styled.Text`
  ${Typography.Responsive.Body}
  text-align: center;
  justify-content: center;
  color: ${colors.gray.secondary};
`;

const MessageText = Styled.Text`
  ${Typography.Responsive.Body}
  text-align: center;
  justify-content: center;
  color: ${colors.gray.tertiary};
`;

const Line = Styled.View`
  height: 1px;
  background-color: ${colors.gray.border};
`;

const Container = Styled.View`
  margin-top: 20px;
`;

interface OutgoingCallDrawerBodyProps {
  search: string;
  handleUpdateSearch: (value: string) => void;
  filteredSalesLeads: any;
  loading: boolean;
  handlePhoneNumberSelected: (toNumber: any) => void;
  submitting: boolean;
  form: any;
  selectedSalesLeadName: string;
  selectedSalesLeadId: string;
  responsive: any;
}

const OutgoingCallDrawerBody = ({
  search,
  handleUpdateSearch,
  filteredSalesLeads,
  loading,
  handlePhoneNumberSelected,
  submitting,
  form,
  selectedSalesLeadName,
  selectedSalesLeadId,
  responsive,
}: OutgoingCallDrawerBodyProps) => {
  // Check if the search is a valid phone number, we want to strip all non digits away in case the phone number is
  // valid but formatted like (123) 456-7890
  const isStrippedDownSearchValidPhoneNumber = /^(\d{10})$/.test(search.replace(/\D/g, ''));
  const validNumberIsNotInContacts =
    filteredSalesLeads && !filteredSalesLeads.length && isStrippedDownSearchValidPhoneNumber;
  const validPhoneNumberToCall = isStrippedDownSearchValidPhoneNumber
    ? search.replace(/\D/g, '')
    : search;
  const hasNoSearchResults =
    filteredSalesLeads &&
    !filteredSalesLeads.length &&
    search.length > 1 &&
    !validNumberIsNotInContacts;
  const dropdownSheetOptions = filteredSalesLeads?.map((salesLead: any) => ({
    label: salesLead.fullName,
    description: salesLead.phoneNumber ? Phone.display(salesLead.phoneNumber) : 'No phone number',
    value: salesLead.phoneNumber,
    identifier: salesLead.id,
    isDisabled: !salesLead.phoneNumber,
  }));

  const hasNotYetSearched = search.length < 2 && !form.values?.initiateOutgoingCallForm.toNumber;

  useEffect(() => {
    if (isStrippedDownSearchValidPhoneNumber) {
      handlePhoneNumberSelected({toNumber: validPhoneNumberToCall});
    }
  }, [isStrippedDownSearchValidPhoneNumber]);
  return (
    <React.Fragment>
      {form.values?.initiateOutgoingCallForm.toNumber && (
        <Row>
          <CalleeText responsive={responsive}>{selectedSalesLeadName}</CalleeText>
        </Row>
      )}
      {form.values?.initiateOutgoingCallForm.toNumber ? (
        <SearchText responsive={responsive}>
          <FieldInput
            setFieldValue={(_name: any, value: any) => {
              handleUpdateSearch(value);
              form.setFieldValue('initiateOutgoingCallForm.toNumber', '');
            }}
            style={{flex: 1}}
            value={Phone.display(form.values.initiateOutgoingCallForm.toNumber)}
            errors={[form.errors.initiateOutgoingCallForm?.toNumber]}
            isResponsive
            input={{
              placeholder: 'Enter a name, email, or number',
              autoFocus: true,
              style: {
                backgroundColor: colors.gray.background,
                border: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Avenir',
                color: colors.gray.primary,
                fontSize: 22,
                lineHeight: 32,
                textAlign: 'center',
                minWidth: 350,
                fontWeight: 700,
              },
            }}
          />
        </SearchText>
      ) : (
        <React.Fragment>
          <Space height={24} />
          <SearchText responsive={responsive}>
            <FieldInput
              setFieldValue={(_name: any, value: any) => {
                handleUpdateSearch(value);
              }}
              name={'toNumber'}
              errors={form.errors.initiateOutgoingCallForm}
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
              touched={{toNumber: true}}
              isResponsive
              input={{
                placeholder: 'Enter a name, email, or number',
                autoFocus: true,
                style: {
                  backgroundColor: colors.gray.background,
                  border: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Avenir',
                  color: colors.gray.primary,
                  fontSize: 22,
                  lineHeight: 32,
                  textAlign: 'center',
                  minWidth: 350,
                  fontWeight: 700,
                },
              }}
            />
          </SearchText>
        </React.Fragment>
      )}
      <Space height={32} />
      {loading ? (
        <Container style={{justifyContent: 'center'}}>
          <PageLoadingIndicator />
        </Container>
      ) : (
        <React.Fragment>
          {hasNotYetSearched && (
            <React.Fragment>
              <Row>
                <MessageText responsive={responsive}>
                  Results will appear when you enter a name, email, or phone number.
                </MessageText>
              </Row>
            </React.Fragment>
          )}
          {hasNoSearchResults && (
            <React.Fragment>
              <Row>
                <MessageText responsive={responsive}>
                  '{search}' is not in your contacts list.
                </MessageText>
              </Row>
            </React.Fragment>
          )}
          {validNumberIsNotInContacts && (
            <React.Fragment>
              <Row>
                <MessageText responsive={responsive}>
                  '{Phone.display(validPhoneNumberToCall)}' is not in your contacts list.
                </MessageText>
              </Row>
            </React.Fragment>
          )}
          {dropdownSheetOptions?.map((dropdownOption: any) => (
            <React.Fragment key={dropdownOption.identifier}>
              <DropdownSheetOption
                option={dropdownOption}
                showDescriptionInOption
                isSelected={selectedSalesLeadId === dropdownOption.identifier}
                handleOption={() => {
                  handlePhoneNumberSelected({
                    toNumber: dropdownOption.value,
                    toName: dropdownOption.label,
                    toId: dropdownOption.identifier,
                  });
                }}
              />
              <Line />
            </React.Fragment>
          ))}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

OutgoingCallDrawerBody.fragment = gql`
  fragment OutgoingCallDrawerBody on Organization {
    filteredSalesLeads(searchQuery: $searchQuery) {
      id
      fullName
      email
      phoneNumber
    }
  }
`;

export default OutgoingCallDrawerBody;
